/**
 * RowEditor — редактор одной строки таблицы шага «Счета СФУ».
 *
 * Колонки: группа услуг (с подписью типа счёта) | IBAN | BIC | Скан реквизитов
 *
 * Атомарный flow (2026-05-04):
 *  - Поля IBAN/BIC и дроп-зона активны сразу — пользователь может вводить и
 *    загружать файл в любом порядке.
 *  - Тип счёта (CURRENT/SAVINGS) определяется группой услуг и отображается
 *    read-only под названием группы — селекта больше нет.
 *  - Когда строка «готова» (IBAN валиден по checkIban, BIC выбран из справочника,
 *    плюс либо скан в state, либо выбран reuse-source) — useEffect вызывает vm.submitRow,
 *    который шлёт один атомарный POST /api/Registrations/{id}/sfu-accounts.
 *  - Для reuse: выбор существующего IBAN из Autocomplete устанавливает
 *    sourceRegistrationAccountId; submitRow создаёт клон счёта и копирует
 *    скан-документ одной транзакцией на бэке.
 *  - После успеха поля становятся read-only, у chip'а скана появляется
 *    кнопка удалить — она удаляет всю строку через DELETE
 *    /api/RegistrationAccounts/{id} (cascade на бэке очистит RegistrationDoc/Scan).
 */
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from '@shared/utils/i18n';
import { useDropzone } from 'react-dropzone';
import logger from 'js-logger';
import { fileToScanDoc, getFileErrorMessage } from '@shared/utils/files';
import { notifications } from '@shared/utils/notifications';
import { checkIban, KZ_IBAN_LENGTH } from '@shared/utils/helpers/validation/validateIban';
import type { DbBank } from '@/shared/api/orval/models/dbBank';
import type {
  ChangeUnionTypeAccountsViewModel,
  RowState,
  RowAccountTypeCode
} from '../model/viewModel';

interface IbanOption {
  /** registrationAccountId source-строки (для setRowReuseSource) */
  sourceId: number;
  iban: string;
  serviceGroupName: string;
  /** Готовая label для рендера в выпадающем списке */
  label: string;
}

interface RowEditorProps {
  row: RowState;
  vm: ChangeUnionTypeAccountsViewModel;
  banks: DbBank[];
}

export const RowEditor: React.FC<RowEditorProps> = observer(({ row, vm, banks }) => {
  const { t } = useTranslation();

  const isLocked = !!row.registrationAccountId;
  const isReuseSelected = !!row.sourceRegistrationAccountId;

  // Опции для Autocomplete IBAN — все ранее сохранённые счета этой же заявки,
  // кроме самой текущей строки. Дубли IBAN отфильтровываются.
  const ibanOptions: IbanOption[] = (() => {
    const seen = new Set<string>();
    const list: IbanOption[] = [];
    for (const r of vm.completedRows) {
      if (r.serviceGroupId === row.serviceGroupId) continue;
      if (!r.registrationAccountId || !r.iban) continue;
      if (seen.has(r.iban)) continue;
      seen.add(r.iban);
      list.push({
        sourceId: r.registrationAccountId,
        iban: r.iban,
        serviceGroupName: r.serviceGroupNameRu,
        label: t('registration:wizard.changeUnionType.ibanOptionLabel', {
          iban: r.iban,
          group: r.serviceGroupNameRu
        })
      });
    }
    return list;
  })();
  const hasIbanOptions = ibanOptions.length > 0;

  // Helper: автоподстановка BIC по IBAN через каталог банков.
  // bicId = iban.slice(4,7) → bank.identifier === bicId → bank.bic.
  const tryAutofillBic = (iban: string): void => {
    if (iban.length < 7) return;
    const bicId = iban.slice(4, 7);
    const bank = banks.find((b) => b.identifier === bicId);
    if (!bank?.bic) return;
    vm.setRowBic(row.serviceGroupId, bank.bic);
  };

  // ─── IBAN: ввод текстом ────────────────────────────────────────────────────
  // OSI-806: нормализуем ввод (uppercase + без пробелов) — checkIban строгий, как бэк,
  // и сам ничего не чистит; так в валидатор и на бэк уходит одна и та же строка.
  const handleIbanInputChange = (value: string) => {
    const normalized = value.toUpperCase().replace(/\s/g, '');
    vm.setRowIban(row.serviceGroupId, normalized);
    tryAutofillBic(normalized);
  };

  // ─── IBAN: выбор существующей опции (reuse) ────────────────────────────────
  const handleIbanOptionSelect = (option: IbanOption | string | null) => {
    if (!option || typeof option === 'string') return;
    vm.setRowReuseSource(row.serviceGroupId, option.sourceId);
  };

  // ─── Drag-and-drop файла ─────────────────────────────────────────────────────
  // Активна сразу — буфер файла кладётся в state и отправится атомарным submit'ом
  // вместе с IBAN/BIC. Блокируется только когда строка уже сохранена или идёт submit.
  const { getRootProps, getInputProps } = useDropzone({
    noClick: false,
    noKeyboard: false,
    maxFiles: 1,
    disabled: isLocked || row.isSaving,
    accept: ['image/*', 'application/pdf'],
    onDrop: async (files: File[]) => {
      if (!files.length) return;
      // Без try/catch ошибка подготовки файла (не влез в бюджет / битый файл) проваливалась
      // молча: dropzone её глотал, chip не появлялся, пользователь не понимал что произошло.
      try {
        const scanDoc = await fileToScanDoc(files[0], 'IBAN_INFO');
        vm.setRowScan(
          row.serviceGroupId,
          files[0].name,
          scanDoc.extension ?? '',
          scanDoc.data ?? ''
        );
      } catch (e) {
        logger.error(e);
        notifications.toast(getFileErrorMessage(e), 'error');
      }
    }
  });

  // ─── Автосабмит при готовности ─────────────────────────────────────────────
  // Триггер: любое изменение iban/bic/scan/reuse-source. submitRow сам проверяет
  // готовность и no-op при уже сохранённой строке (защита от двойных вызовов).
  useEffect(() => {
    if (isLocked) return;
    if (row.isSaving) return;
    // OSI-806: автосабмит только по валидному IBAN (формат + контрольная сумма),
    // а не просто по длине 20 — иначе мусорный счёт улетал на бэк и возвращался тостом.
    const ibanOk = checkIban(row.iban.trim());
    const bicOk = row.bic.trim().length === 8;
    const hasScan = !!(row.scanExtension && row.scanBase64Data);
    const hasReuse = !!row.sourceRegistrationAccountId;
    if (ibanOk && bicOk && (hasScan || hasReuse)) {
      vm.submitRow(row.serviceGroupId);
    }
    // зависимости: всё что влияет на готовность
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    row.iban,
    row.bic,
    row.scanBase64Data,
    row.sourceRegistrationAccountId,
    isLocked,
    row.isSaving
  ]);

  const isComplete = isLocked && !!row.scanDocId;
  const hasScanBuffered = !!row.scanFileName;

  // Валидация (инлайн).
  // OSI-806: два уровня сообщения — пока пользователь набирает, говорим про длину,
  // а на полной длине проверяем формат казахстанского IBAN и контрольную сумму mod-97.
  const ibanValue = row.iban.trim();
  const ibanError = (() => {
    if (ibanValue.length === 0) return '';
    if (ibanValue.length !== KZ_IBAN_LENGTH) {
      return t('registration:wizard.changeUnionType.errors.invalidIban');
    }
    return checkIban(ibanValue) ? '' : t('registration:wizard.changeUnionType.errors.invalidIbanFormat');
  })();
  const bicError =
    row.bic.length > 0 && row.bic.trim().length !== 8
      ? t('registration:wizard.changeUnionType.errors.invalidBic')
      : '';

  // ─── BIC: выпадающий список по справочнику банков ──────────────────────────
  // OSI-806 (дефект 2): было обычное текстовое поле, хотя в кабинетных диалогах
  // (features/osi/accounts/ui/EditAccountButton) БИК выбирается из каталога банков.
  // В модели СФУ bic хранится СТРОКОЙ (и такой же уходит на бэк), поэтому маппим
  // строку ↔ объект банка прямо здесь, не меняя формат RowState/запроса.
  const selectedBank: DbBank | null = row.bic
    ? banks.find((b) => b.bic === row.bic) ?? { bic: row.bic, name: '' }
    : null;
  // Если БИК пришёл с бэка (уже сохранённая строка), а в каталоге его нет —
  // подмешиваем синтетическую опцию, чтобы MUI не ругался «value не из options»
  // и пользователь видел фактическое значение.
  const bankOptions: DbBank[] =
    selectedBank && !banks.some((b) => b.bic === selectedBank.bic) ? [selectedBank, ...banks] : banks;

  // Лейбл типа счёта (из i18n) — показываем под названием группы.
  const accountTypeLabel = (code: RowAccountTypeCode) =>
    t(`registration:wizard.changeUnionType.accountTypes.${code}`);

  return (
    <TableRow key={row.serviceGroupId} sx={{ verticalAlign: 'top' }}>
      {/* Группа услуг + read-only тип счёта подписью */}
      <TableCell sx={{ minWidth: 180, pt: 2 }}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2">{row.serviceGroupNameRu}</Typography>
          {isComplete && <CheckCircleIcon fontSize="small" color="success" />}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {accountTypeLabel(row.serviceGroupAccountTypeCode)}
        </Typography>
      </TableCell>

      {/* IBAN — Autocomplete (freeSolo) если есть опции для reuse, иначе обычный TextField */}
      <TableCell sx={{ pt: 1 }}>
        {hasIbanOptions ? (
          <Autocomplete<IbanOption, false, false, true>
            freeSolo
            size="small"
            options={ibanOptions}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
            value={row.iban}
            inputValue={row.iban}
            disabled={isLocked || row.isSaving}
            onInputChange={(_e, value, reason) => {
              if (reason === 'input' || reason === 'clear') {
                handleIbanInputChange(value);
              }
            }}
            onChange={(_e, value) => {
              if (value && typeof value !== 'string') {
                handleIbanOptionSelect(value);
              }
            }}
            isOptionEqualToValue={(opt, val) =>
              typeof val === 'string' ? opt.iban === val : opt.iban === val.iban
            }
            renderOption={(props, option) => (
              <li {...props} key={option.sourceId}>
                <Typography variant="body2">{option.label}</Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="KZ…"
                error={!!ibanError}
                helperText={ibanError}
                inputProps={{
                  ...params.inputProps,
                  maxLength: KZ_IBAN_LENGTH,
                  'aria-label': t('registration:wizard.changeUnionType.columns.iban'),
                  tabIndex: 1
                }}
                sx={{ width: 240 }}
              />
            )}
          />
        ) : (
          <TextField
            size="small"
            value={row.iban}
            placeholder="KZ…"
            onChange={(e) => handleIbanInputChange(e.target.value)}
            disabled={isLocked || row.isSaving}
            error={!!ibanError}
            helperText={ibanError}
            inputProps={{
              maxLength: KZ_IBAN_LENGTH,
              'aria-label': t('registration:wizard.changeUnionType.columns.iban'),
              tabIndex: 1
            }}
            sx={{ width: 240 }}
          />
        )}
      </TableCell>

      {/* BIC — выпадающий список по справочнику банков; авто-подставляется по IBAN.
          freeSolo НЕ включаем: произвольный БИК отправить нельзя, только выбор из каталога. */}
      <TableCell sx={{ pt: 1 }}>
        <Autocomplete<DbBank, false, false, false>
          size="small"
          options={bankOptions}
          value={selectedBank}
          disabled={isLocked || isReuseSelected || row.isSaving}
          getOptionLabel={(option) => option.bic || ''}
          isOptionEqualToValue={(option, value) => option.bic === value.bic}
          onChange={(_e, value) => vm.setRowBic(row.serviceGroupId, value?.bic ?? '')}
          renderOption={(props, option) => (
            <li {...props} key={option.bic}>
              <Typography variant="body2">{`${option.bic} - ${option.name}`}</Typography>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="KCJBKZKX"
              error={!!bicError}
              helperText={bicError}
              inputProps={{
                ...params.inputProps,
                'aria-label': t('registration:wizard.changeUnionType.columns.bic'),
                tabIndex: 2
              }}
              sx={{ width: 170 }}
            />
          )}
        />
      </TableCell>

      {/* Скан реквизитов */}
      <TableCell sx={{ pt: 1 }}>
        {row.isSaving ? (
          <CircularProgress size={20} />
        ) : isLocked && row.scanDocId ? (
          // Строка успешно сохранена — chip с кнопкой удалить (cascade DELETE на бэке).
          <Chip
            label={t('registration:wizard.changeUnionType.buttons.scanUploaded')}
            color="success"
            size="small"
            variant="outlined"
            onDelete={() => vm.removeRow(row.serviceGroupId)}
          />
        ) : isReuseSelected ? (
          // Выбран reuse-source — скан копируется с бэка, файл загружать не нужно.
          <Chip
            label={t('registration:wizard.changeUnionType.buttons.reuseSelected')}
            color="info"
            size="small"
            variant="outlined"
          />
        ) : hasScanBuffered ? (
          // Файл выбран и буферизован — ждём заполнения IBAN/BIC чтобы автосабмит сработал.
          <Chip
            label={row.scanFileName}
            color="primary"
            size="small"
            variant="outlined"
            onDelete={() => vm.clearRowScan(row.serviceGroupId)}
          />
        ) : (
          <Box
            {...getRootProps()}
            sx={{
              border: '1px dashed',
              borderColor: 'primary.main',
              borderRadius: 1,
              p: 1,
              cursor: 'pointer',
              minWidth: 120,
              textAlign: 'center'
            }}
            tabIndex={3}
          >
            <input {...getInputProps()} />
            <Typography variant="caption" color="primary">
              {t('registration:wizard.changeUnionType.buttons.uploadScan')}
            </Typography>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
});
